import React, { useState, useEffect, useCallback } from 'react';
import {
  getAdminUsers,
  toggleUserStatus,
  deleteAdminUser,
} from '../../../services/adminService';
import {
  Search, Filter, Users, RefreshCw, ChevronLeft,
  ChevronRight, AlertCircle, X, CheckCircle2, XCircle,
  Stethoscope, UserRound, ShieldCheck, Trash2,
} from 'lucide-react';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const ROLE_CFG = {
  doctor:  { label: 'Médico',    cls: 'bg-teal-100 text-teal-700',   Icon: Stethoscope },
  patient: { label: 'Paciente',  cls: 'bg-blue-100 text-blue-700',   Icon: UserRound },
  admin:   { label: 'Admin',     cls: 'bg-purple-100 text-purple-700', Icon: ShieldCheck },
};

const RoleBadge = ({ role }) => {
  const cfg = ROLE_CFG[role] || { label: role, cls: 'bg-gray-100 text-gray-600', Icon: UserRound };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>
      <cfg.Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

const StatusDot = ({ active }) => active
  ? <span className="inline-flex items-center gap-1 text-xs text-green-700"><span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />Activo</span>
  : <span className="inline-flex items-center gap-1 text-xs text-gray-400"><span className="w-1.5 h-1.5 rounded-full bg-gray-300 inline-block" />Inactivo</span>;

const fullNameOf = (user) =>
  `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`.trim();

// Comparación tolerante a mayúsculas y espacios de más: lo que se busca es que
// el admin lea el nombre de quien va a borrar, no que acierte cada espacio.
const normalizeName = (s) => (s || '').trim().replace(/\s+/g, ' ').toLowerCase();

const APPT_STATUS_ES = {
  scheduled: 'Programada', confirmed: 'Confirmada', 'in-progress': 'En curso',
};

const fmtDateTime = (d) => d
  ? new Date(d).toLocaleString('es-CO', { timeZone: 'America/Bogota', dateStyle: 'medium', timeStyle: 'short' })
  : '—';

// ─── Delete Modal ────────────────────────────────────────────────────────────
// Pide escribir el nombre completo para confirmar. Si el backend responde 409
// por citas activas, las lista para que el admin las resuelva antes.
export const DeleteUserModal = ({ user, onClose, onDeleted }) => {
  const [typed, setTyped]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [activeAppointments, setActiveAppointments] = useState([]);

  const fullName = fullNameOf(user);
  const matches  = fullName !== '' && normalizeName(typed) === normalizeName(fullName);

  const handleDelete = async () => {
    if (!matches) return;
    setLoading(true); setError(null); setActiveAppointments([]);
    try {
      await deleteAdminUser(user._id);
      onDeleted(user._id);
    } catch (e) {
      const data = e?.response?.data;
      setError(data?.message || 'Error al eliminar el usuario');
      setActiveAppointments(data?.data?.activeAppointments || []);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-labelledby="delete-user-title">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id="delete-user-title" className="text-base font-semibold text-gray-900">Eliminar usuario</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400" aria-label="Cerrar"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4 text-sm">
          <p className="text-gray-700">
            Se cerrarán sus sesiones y se borrarán su correo y su teléfono. Sus citas, pagos e historias
            clínicas se conservan. <strong>No se puede deshacer.</strong>
          </p>
          <label className="block">
            <span className="text-gray-600">Escribe <strong>{fullName || '(sin nombre)'}</strong> para confirmar</span>
            <input
              value={typed}
              onChange={e => setTyped(e.target.value)}
              autoFocus
              aria-label="Nombre completo del usuario"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </label>

          {error && (
            <div className="rounded-lg bg-red-50 text-red-700 p-3 space-y-2">
              <p className="flex items-start gap-2"><AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />{error}</p>
              {activeAppointments.length > 0 && (
                <ul className="list-disc pl-6 space-y-0.5 text-xs">
                  {activeAppointments.map(a => (
                    <li key={a.id}>
                      {fmtDateTime(a.scheduledDate)} · {APPT_STATUS_ES[a.status] || a.status}
                      <span className="text-red-400 font-mono"> ({a.id})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50">Cancelar</button>
          <button
            onClick={handleDelete}
            disabled={!matches || loading}
            className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── User Drawer ─────────────────────────────────────────────────────────────
const UserDrawer = ({ user, onClose, onToggle, onDelete, loading }) => {
  if (!user) return null;
  const fullName = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim();
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-sm bg-white shadow-xl flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Detalle del usuario</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 px-6 py-5 space-y-5">
          {/* avatar placeholder */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              {React.createElement(ROLE_CFG[user.role]?.Icon || UserRound, { className: 'w-6 h-6 text-gray-400' })}
            </div>
            <div>
              <p className="font-semibold text-gray-900">{fullName || '(sin nombre)'}</p>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>

          {/* status toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
            <div>
              <p className="text-xs text-gray-500 mb-1">Estado de la cuenta</p>
              <StatusDot active={user.isActive} />
            </div>
            {user.role !== 'admin' && (
              <button
                onClick={() => onToggle(user._id)}
                disabled={loading}
                className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
                  user.isActive
                    ? 'bg-red-50 text-red-600 hover:bg-red-100'
                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                } disabled:opacity-50`}>
                {loading
                  ? <RefreshCw className="w-4 h-4 animate-spin" />
                  : user.isActive ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                {user.isActive ? 'Desactivar' : 'Activar'}
              </button>
            )}
          </div>

          {user.role !== 'admin' && (
            <button
              onClick={() => onDelete(user)}
              className="w-full flex items-center justify-center gap-2 text-sm font-medium px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />Eliminar usuario
            </button>
          )}

          {/* fields */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Rol',          value: <RoleBadge role={user.role} /> },
              { label: 'Teléfono',     value: user.profile?.phone || '—' },
              { label: 'Miembro desde', value: fmtDate(user.createdAt) },
              { label: 'ID',            value: <span className="font-mono text-xs break-all">{user._id}</span> },
            ].map(({ label, value }) => (
              <div key={label} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                <div className="text-sm font-medium text-gray-800">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main UsersSection ────────────────────────────────────────────────────────
export default function UsersSection() {
  const [users,      setUsers]      = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [filterRole,  setFilterRole]  = useState('');
  const [page,        setPage]        = useState(1);
  const [selected,    setSelected]    = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toDelete,    setToDelete]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { page, limit: 12 };
      if (filterRole) params.role   = filterRole;
      if (search)     params.search = search;
      const { data, pagination: pg } = await getAdminUsers(params);
      setUsers(data);
      setPagination(pg);
    } catch (e) {
      setError(e?.response?.data?.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [page, filterRole, search]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (userId) => {
    setActionLoading(true);
    try {
      const updated = await toggleUserStatus(userId);
      setUsers(prev => prev.map(u =>
        u._id === userId ? { ...u, isActive: updated.isActive } : u
      ));
      if (selected?._id === userId) {
        setSelected(prev => ({ ...prev, isActive: updated.isActive }));
      }
    } catch (e) {
      alert(e?.response?.data?.message || 'Error al cambiar estado');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleted = (userId) => {
    setToDelete(null);
    setSelected(prev => (prev?._id === userId ? null : prev));
    setUsers(prev => prev.filter(u => u._id !== userId));
    setPagination(prev => ({ ...prev, total: Math.max(0, (prev.total || 0) - 1) }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Gestión de Usuarios</h2>
          <p className="text-sm text-gray-500">{pagination.total} usuarios registrados</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-teal-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:border-teal-300 transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Actualizar
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 min-w-48">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Buscar por nombre o email…"
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          <button type="submit" className="px-3 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors">Buscar</button>
        </form>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select value={filterRole} onChange={e => { setFilterRole(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500">
            <option value="">Todos los roles</option>
            <option value="doctor">Médicos</option>
            <option value="patient">Pacientes</option>
            <option value="admin">Admins</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {error ? (
        <div className="flex flex-col items-center justify-center h-48 gap-3 text-red-500">
          <AlertCircle className="w-8 h-8" />
          <p className="text-sm">{error}</p>
          <button onClick={load} className="text-sm text-teal-600 hover:underline">Reintentar</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="animate-pulse p-4 space-y-3">
              {[...Array(6)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Users className="w-10 h-10 mb-3 text-gray-200" />
              <p className="text-sm">No se encontraron usuarios</p>
              {(search || filterRole) && (
                <button onClick={() => { setSearch(''); setSearchInput(''); setFilterRole(''); setPage(1); }}
                  className="mt-2 text-sm text-teal-600 hover:underline">Limpiar filtros</button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Usuario','Rol','Estado','Registrado',''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map(u => {
                    const fullName = u.profile
                      ? `${u.profile.firstName} ${u.profile.lastName}`
                      : '(sin nombre)';
                    return (
                      <tr key={u._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{fullName}</p>
                          <p className="text-xs text-gray-400">{u.email}</p>
                        </td>
                        <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                        <td className="px-4 py-3"><StatusDot active={u.isActive} /></td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(u.createdAt)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button onClick={() => setSelected(u)}
                              className="text-xs text-teal-600 hover:underline">Ver</button>
                            {u.role !== 'admin' && (
                              <button
                                onClick={() => handleToggle(u._id)}
                                disabled={actionLoading}
                                className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                                  u.isActive
                                    ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                                } disabled:opacity-50`}>
                                {u.isActive ? 'Desactivar' : 'Activar'}
                              </button>
                            )}
                            {u.role !== 'admin' && (
                              <button
                                onClick={() => setToDelete(u)}
                                className="text-xs px-2.5 py-1 rounded-lg font-medium text-red-600 hover:bg-red-50 transition-colors">
                                Eliminar
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">Página {pagination.page} de {pagination.pages} · {pagination.total} usuarios</p>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1 || loading}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= pagination.pages || loading}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Drawer */}
      {selected && (
        <UserDrawer
          user={selected}
          onClose={() => setSelected(null)}
          onToggle={handleToggle}
          onDelete={setToDelete}
          loading={actionLoading}
        />
      )}

      {toDelete && (
        <DeleteUserModal
          user={toDelete}
          onClose={() => setToDelete(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}
